/**
 * Beta invites API client — public lookup + authenticated redeem.
 */
import { fetchApi } from './client';
import { log } from '@/lib/logger';
import { MUTATION_TIMEOUT_MS, SCREEN_READ_TIMEOUT_MS } from './requestTimeout';

log.info('[api/betaInvites] Module loaded');

/**
 * Mirrors the payload GET /beta-invites/:slug actually returns
 * (packages/server/src/routes/betaInvites.ts:178). `email` and `role` used to
 * be declared here and were never sent: the route stopped returning the full
 * address in the PII-reduction change and sends `emailHint` instead, and it has
 * never sent `role` at all. Both read as `undefined` at runtime.
 */
export interface BetaInviteLookup {
  firstName: string;
  company: string;
  domain: string;
  betaDays: number;
  status: 'pending' | 'redeemed' | 'expired' | 'reserved';
  /** Masked address for pre-fill, e.g. `e*****@example.com`. Absent when the invite has no email. */
  emailHint?: string;
}

export interface BetaRedeemResult {
  teamId: string;
  betaExpiresAt: string;
  betaDays: number;
  plan: string;
}

export interface BetaReserveResult {
  reserved: boolean;
}

export const betaInvitesApi = {
  /** Public lookup — no auth required */
  lookup: (slug: string) =>
    fetchApi<BetaInviteLookup>(`/beta-invites/${slug}`, { timeoutMs: SCREEN_READ_TIMEOUT_MS }),

  /** Reserve — no auth, just email capture */
  reserve: (slug: string, email: string, name: string) =>
    fetchApi<BetaReserveResult>(`/beta-invites/${slug}/reserve`, {
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify({ email, name }),
    }),

  /** Redeem — requires auth (Clerk JWT) */
  redeem: (token: string | undefined, slug: string, teamId: string) =>
    fetchApi<BetaRedeemResult>(`/beta-invites/${slug}/redeem`, {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify({ teamId }),
    }),
};
